// 클레임 등록(단건) 화면 순수 함수 — 송장 형식 · 조회 결과 → 상품 줄(주문별 묶음) · 2.0·1.0 클레임 불러오기 · 저장 본문
// · 등록 막힘 사유 · 금액이 빈 이유 · 기한 표기.
// 서버 routers/claim_ledger.py · services/claim_ledger.parse 와 칸 이름이 같다. 금액 계산은 서버(미리보기)만 한다 — 여기서 셈하지 않는다.
// 서버 services/claim_bulk.build_body 와 같은 규칙 — 바꾸면 둘 다.

export const INVOICE_ERROR = '송장번호는 숫자 8~20자여야 합니다.';
export const ITEMS_MAX = 100;            // 서버 상품 줄 상한과 같다
export const LOGISTICS_KEYS = ['work_fee', 'pack_fee', 'reship_fee', 'return_fee'];

export const EMPTY_DRAFT = {
  accident: 'general', reason_id: '', extent: '', fault: '', handling: '', temp_zone: '', center_key: '',
  shipper_id: '', shipper_name: '', ship_date: '', delivered_date: '', occurred_date: '', memo: '',
  reship_invoice_no: '', pack_code: '', restocked: false, manual_product_amount: '', manual_logistics_amount: '',
  logistics_inputs: {}, intake_channel: '', compensation_method: '',
};

/**
 * 접수 경로·배상 방식 선택 목록(form 의 …_options · 보상기준 마스터 설정) → select 옵션 [{value, label}].
 * 지금 값이 목록에 없으면(목록이 바뀌기 전에 저장한 값 · 옛 자유 글자) «<값> (이전 값)» 한 줄을 더한다.
 */
export function optionsWithCurrent(list, current) {
  const values = (Array.isArray(list) ? list : []).filter((v) => typeof v === 'string' && v);
  const out = values.map((v) => ({ value: v, label: v }));
  const cur = String(current ?? '');
  if (cur && !values.includes(cur)) out.push({ value: cur, label: `${cur} (이전 값)` });
  return out;
}

/** 조회 결과 → 자동으로 채운 원값(배지 「수정됨」 비교 기준 · draft 와 같은 칸 이름) — 출고정보가 없으면 전부 빈 값. */
export function autoOriginFrom(lookup) {
  const s = lookup && lookup.found ? lookup.shipment || {} : {};
  return {
    center_key: s.center_key || '',
    shipper_id: s.shipper_id ? String(s.shipper_id) : '',
    shipper_name: s.shipper_id ? '' : (s.shipper_name || ''),
    ship_date: s.ship_date || '',
    delivered_date: s.delivered_date || '',
    temp_zone: s.temp_zone || '',
  };
}

/** 칸 값이 자동 원값과 다른가(원값이 비어 있던 칸에 사람이 넣은 값도 다르다). */
export function isEdited(draft, origin, keys) {
  return keys.some((key) => String((draft && draft[key]) ?? '') !== String((origin && origin[key]) ?? ''));
}

/** 상품 표 칸(열)에 사람이 넣은 값이 있는가 — 직접 입력 줄의 그 칸이 비어 있지 않으면 참. */
export function columnEdited(rows, key) {
  return (rows || []).some((row) => row.manual && String(row[key] ?? '').trim() !== '');
}

export const digitsOnly = (text) => String(text ?? '').replace(/[^0-9]/g, '');

/** 송장 입력 → 보낼 숫자(하이픈·공백 제거) · 형식이 틀리면 error. */
export function checkInvoice(text) {
  const raw = String(text ?? '').replace(/[\s-]/g, '');
  if (!/^\d{8,20}$/.test(raw)) return { value: '', error: INVOICE_ERROR };
  return { value: raw, error: '' };
}

/** 입력 글자 → 정수(빈 칸·숫자 아님 = null · 콤마 허용). */
export function intOrNull(text) {
  const s = String(text ?? '').replace(/,/g, '').trim();
  if (!s || !/^-?\d+$/.test(s)) return null;
  return Number(s);
}

let lineSeq = 0;
const nextKey = () => { lineSeq += 1; return `row-${lineSeq}`; };

/** 조회 결과 줄 → 화면 줄(체크·수량은 서버 default_checked 대로 — 한 포장번호에 주문이 여럿이면 서버가 해제 · 클레임 수량 기본 = 출고수량). */
export function rowsFromLookup(items) {
  return (Array.isArray(items) ? items : []).slice(0, ITEMS_MAX).map((it) => ({
    key: nextKey(),
    manual: false,
    line_key: it.line_key || null,
    od_sno: it.od_sno || null,
    prod_cd: it.prod_cd || '',
    item_cd: it.item_cd || '',
    prod_name: it.prod_name || '',
    out_qty: Number(it.out_qty) || 0,
    qty: String(it.default_checked ? (Number(it.out_qty) || 1) : 0),
    sale_price: it.sale_price ?? null,
    pay_amount: it.pay_amount ?? null,
    pur_price: it.pur_price ?? null,
    unit_price_input: '',
    selected: !!it.default_checked,
  }));
}

/** 직접 입력 줄 하나(출고정보가 없을 때 · [+ 상품 추가]). */
export function blankRow() {
  return {
    key: nextKey(), manual: true, line_key: null, od_sno: null, prod_cd: '', item_cd: '', prod_name: '', out_qty: 0,
    qty: '1', sale_price: '', pay_amount: '', pur_price: '', unit_price_input: '', selected: true,
  };
}

/** 저장한 원장 → 화면 줄 — 계산 근거의 전체 줄(선택 표시 포함)이 있으면 그것, 없으면 원장 상품 줄. */
export function rowsFromClaim(claim) {
  const basis = claim && claim.calc_basis;
  const all = basis && Array.isArray(basis.items) ? basis.items : null;
  if (all && all.length) {
    return all.map((it) => ({
      key: nextKey(),
      manual: !it.line_key,
      line_key: it.line_key || null,
      od_sno: it.od_sno || null,
      prod_cd: it.prod_cd || '',
      item_cd: it.item_cd || '',
      prod_name: it.prod_name || '',
      out_qty: Number(it.out_qty) || 0,
      qty: String(it.qty ?? 0),
      sale_price: it.sale_price ?? (it.line_key ? null : ''),
      pay_amount: it.pay_amount ?? (it.line_key ? null : ''),
      pur_price: it.pur_price ?? (it.line_key ? null : ''),
      unit_price_input: it.unit_price_input == null ? '' : String(it.unit_price_input),
      selected: it.selected !== false,
    }));
  }
  return ((claim && claim.items) || []).map((it) => ({
    ...blankRow(), prod_cd: it.prod_cd || '', item_cd: it.item_cd || '', prod_name: it.prod_name || '', qty: String(it.qty ?? 0),
  }));
}

/**
 * 상품 줄 → 주문별 묶음 [{od_sno, rows}](받은 순서). 주문번호가 없는 줄(직접 입력 줄 포함)은 끝에 od_sno null 한 묶음.
 */
export function groupRowsByOrder(rows) {
  const groups = [];
  const byOrder = new Map();
  const rest = [];
  (rows || []).forEach((row) => {
    if (row.manual || !row.od_sno) { rest.push(row); return; }
    const key = String(row.od_sno);
    let group = byOrder.get(key);
    if (!group) {
      group = { od_sno: row.od_sno, rows: [] };
      byOrder.set(key, group);
      groups.push(group);
    }
    group.rows.push(row);
  });
  if (rest.length) groups.push({ od_sno: null, rows: rest });
  return groups;
}

/** 상품 표 위 요약 — 포장번호에 송장이 여럿이면 «같은 포장번호로 송장 N개 · 주문 M건», 아니고 주문이 여럿이면 «주문 M건», 아니면 ''. */
export function packSummaryText(shipment) {
  const s = shipment || {};
  const invoices = Number(s.invoice_count) || 0;
  const orders = Number(s.order_count) || 0;
  if (invoices > 1) return `같은 포장번호로 송장 ${invoices}개 · 주문 ${orders}건`;
  if (orders > 1) return `주문 ${orders}건`;
  return '';
}

// 사유·범위를 비우는 분류(판단 필요 · 화주·고객 사유) — 서버 claim_master.RULE_LABELS.category 의 키.
const CLEAR_REASON_CATEGORIES = ['review', 'shipper_customer'];

/**
 * 클레임 [불러오기](조회 응답 v2_claims · v1_claims 의 항목) → {draft, rows, claimRef, notices}. 등록됨이면 null.
 * 줄 = 서버 lines 의 줄만 체크·클레임 수량 = 그 수량 · 나머지 조회 줄은 해제·수량 0(직접 입력 줄은 그대로).
 * draft = 처리 방식(defaults.handling · 없으면 비움) · 발생일자 · 메모(remark) · 재출고 송장
 *   · 귀책(defaults.fault 가 있으면 그 값 · 판단 필요면 비움) · 판단 필요·화주·고객 사유면 사유·범위를 비운다.
 * claimRef = 저장 본문에 펼칠 칸(서버 parse) — 2.0 {v2_claim_no, v2_reason_cd, v2_reason_detail_cd, v2_claim_type}
 *   · 1.0 {v1_claim_key, v1_claim_status, v1_reason_cd, v1_reason_name}. notices = 서버 안내 문구 그대로.
 */
export function applyLoadedClaim(item, source, rows) {
  if (!item || item.registered) return null;
  const qtyByLine = new Map((Array.isArray(item.lines) ? item.lines : [])
    .filter((line) => line && line.line_key != null)
    .map((line) => [String(line.line_key), line.qty]));
  const nextRows = (rows || []).map((row) => {
    if (row.manual) return row;
    const key = row.line_key == null ? null : String(row.line_key);
    if (key !== null && qtyByLine.has(key)) {
      return { ...row, selected: true, qty: String(Number(qtyByLine.get(key)) || row.out_qty || 1) };
    }
    return { ...row, selected: false, qty: '0' };
  });
  const defaults = item.defaults || {};
  const draft = {
    handling: defaults.handling ?? '',
    occurred_date: item.occurred_date || '',
    memo: item.remark || '',
    reship_invoice_no: item.reship_invoice_no || '',
  };
  if (defaults.fault) draft.fault = defaults.fault;
  else if (item.category === 'review') draft.fault = '';
  if (CLEAR_REASON_CATEGORIES.includes(item.category)) {
    draft.reason_id = '';
    draft.extent = '';
  }
  const claimRef = source === 'v1'
    ? {
      v1_claim_key: item.v1_claim_key,
      v1_claim_status: item.claim_status || null,
      v1_reason_cd: item.detail_cd || null,
      v1_reason_name: item.detail_name || null,
    }
    : {
      v2_claim_no: item.v2_claim_no,
      v2_reason_cd: item.reason_cd || null,
      v2_reason_detail_cd: item.reason_detail_cd || null,
      v2_claim_type: item.kind || null,
    };
  return { draft, rows: nextRows, claimRef, notices: Array.isArray(item.notices) ? [...item.notices] : [] };
}

/** 저장한 원장 → claimRef(2.0·1.0 클레임 칸 · 재출고 원 클레임 칸) — 아무 연결도 없으면 null. */
export function claimRefFromClaim(claim) {
  const c = claim || {};
  const ref = {};
  if (c.v2_claim_no) {
    Object.assign(ref, {
      v2_claim_no: c.v2_claim_no, v2_reason_cd: c.v2_reason_cd || null, v2_reason_detail_cd: c.v2_reason_detail_cd || null,
      v2_claim_type: c.v2_claim_type || null,
    });
  } else if (c.v1_claim_key) {
    Object.assign(ref, {
      v1_claim_key: c.v1_claim_key, v1_claim_status: c.v1_claim_status || null, v1_reason_cd: c.v1_reason_cd || null,
      v1_reason_name: c.v1_reason_name || null,
    });
  }
  if (c.reship_of_claim_no) Object.assign(ref, { reship_of_source: c.reship_of_source || null, reship_of_claim_no: c.reship_of_claim_no });
  return Object.keys(ref).length ? ref : null;
}

/** claimRef 표기 «연결된 클레임: 2.0 CLM1 · 원 클레임 72253의 재출고 건» — 없으면 ''. */
export function claimRefText(ref) {
  if (!ref) return '';
  const parts = [];
  if (ref.v2_claim_no) parts.push(`2.0 ${ref.v2_claim_no}`);
  if (ref.v1_claim_key) parts.push(`1.0 ${ref.v1_claim_key}`);
  if (ref.reship_of_claim_no) parts.push(`원 클레임 ${ref.reship_of_claim_no}의 재출고 건`);
  return parts.length ? `연결된 클레임: ${parts.join(' · ')}` : '';
}

/** 클레임 항목 유형 표기 — 교환 · 반품 · 클레임 주문서 없음(1.0 상태 0) · 그 밖은 원본 값(2.0 유형 · 1.0 상태) 그대로. */
export function claimKindText(item, source) {
  if (!item) return '';
  if (item.kind === 'exchange') return '교환';
  if (item.kind === 'return') return '반품';
  if (item.kind === 'none') return '클레임 주문서 없음';
  const raw = source === 'v1' ? item.claim_status : item.type_raw;
  return raw || '유형 미확인';
}

// 미리보기 calc.missing 칸 → 화면 이름(서버 claim_calc.compute 의 missing).
const MISSING_LABELS = { reason_id: '사유', extent: '범위', temp_zone: '품온', handling: '처리 방식', items: '클레임 상품' };

// 받침이 있으면 «을», 없으면 «를»(한글이 아니면 «를»).
const objectParticle = (word) => {
  const code = String(word || '').charCodeAt(String(word || '').length - 1) - 0xac00;
  return code >= 0 && code < 11172 && code % 28 !== 0 ? '을' : '를';
};

/** ③ 금액이 빈 이유 — «사유·범위를 고르면 계산됩니다» · 직접 입력만 남으면 «직접 입력 금액을 넣으면 계산됩니다» · calc 가 없거나 처리 불가면 ''. */
export function amountMissingText(calc) {
  if (!calc || calc.blocked) return '';
  const words = (Array.isArray(calc.missing) ? calc.missing : []).map((key) => MISSING_LABELS[key] || key);
  if (words.length) {
    const joined = words.join('·');
    return `${joined}${objectParticle(joined)} 고르면 계산됩니다`;
  }
  if (Array.isArray(calc.manual_required) && calc.manual_required.length) return '직접 입력 금액을 넣으면 계산됩니다';
  return '';
}

const textOrNull = (v) => {
  const s = String(v ?? '').trim();
  return s || null;
};

/**
 * 화면 상태 → 저장·미리보기 본문(서버 services/claim_ledger.parse 와 같은 칸).
 * claimRef(불러온 2.0·1.0 클레임 · 저장한 원장의 연결)는 본문에 펼친다. 없고 조회가 재출고 송장(lookup.reship_of)이면
 * reship_of_source·reship_of_claim_no 를 넣는다(원 클레임의 재출고 건으로 남는다).
 */
export function buildBody({ draft, rows, lookup, claimRef, invoice, photoCount }, action) {
  const found = !!(lookup && lookup.found);
  const ship = found ? lookup.shipment || {} : {};
  const inputs = {};
  LOGISTICS_KEYS.forEach((key) => {
    const n = intOrNull(draft.logistics_inputs && draft.logistics_inputs[key]);
    if (n !== null) inputs[key] = n;
  });
  const body = {
    action,
    invoice_no: invoice || null,
    snapshot: lookup ? { ...lookup, items: (lookup.items || []).slice(0, ITEMS_MAX) } : null,
    reason_id: intOrNull(draft.reason_id),
    fault: draft.fault || null,
    extent: draft.extent || null,
    handling: draft.handling || null,
    temp_zone: draft.temp_zone || null,
    center_key: draft.center_key || null,
    shipper_id: intOrNull(draft.shipper_id),
    shipper_name: textOrNull(draft.shipper_name),
    shipper_pwn_cd: ship.shipper_pwn_cd || null,
    depot_cd: ship.depot_cd || null,
    courier_source: ship.courier_source || null,
    courier_code: ship.courier_code || null,
    courier_name: ship.courier_name || null,
    ship_date: draft.ship_date || null,
    delivered_date: draft.delivered_date || null,
    occurred_date: draft.occurred_date || null,
    order_no: ship.order_no || null,
    mall_order_no: ship.mall_order_no || null,
    box_count: ship.box_count || 0,
    box_unit_price: ship.box_unit_price ?? null,
    box_work_fee: ship.box_work_fee ?? null,
    pack_code: draft.pack_code || null,
    restocked: !!draft.restocked,
    manual_product_amount: intOrNull(draft.manual_product_amount),
    manual_logistics_amount: intOrNull(draft.manual_logistics_amount),
    logistics_inputs: inputs,
    reship_invoice_no: textOrNull(draft.reship_invoice_no),
    intake_channel: textOrNull(draft.intake_channel),
    compensation_method: textOrNull(draft.compensation_method),
    memo: textOrNull(draft.memo),
    photo_count: photoCount || 0,
    items: rows.slice(0, ITEMS_MAX).map((row) => {
      const qty = intOrNull(row.qty) ?? 0;
      return {
        line_key: row.line_key,
        od_sno: row.od_sno,
        prod_cd: textOrNull(row.prod_cd),
        item_cd: textOrNull(row.item_cd),
        prod_name: textOrNull(row.prod_name),
        out_qty: row.manual ? qty : (row.out_qty || 0),
        qty,
        sale_price: row.manual ? intOrNull(row.sale_price) : row.sale_price,
        pay_amount: row.manual ? intOrNull(row.pay_amount) : row.pay_amount,
        pur_price: row.manual ? intOrNull(row.pur_price) : row.pur_price,
        unit_price_input: intOrNull(row.unit_price_input),
        selected: !!row.selected,
      };
    }),
  };
  const reshipOf = lookup && lookup.reship_of;
  if (claimRef && Object.keys(claimRef).length) Object.assign(body, claimRef);
  else if (reshipOf && reshipOf.claim_no) {
    body.reship_of_source = reshipOf.source || null;
    body.reship_of_claim_no = reshipOf.claim_no;
  }
  return body;
}

/**
 * [등록(승인)] 을 막는 사유(없으면 []) — 필수값 · 처리 불가 · 직접 입력 필요.
 * 귀책 고객사(shipper · 면책)면 사유·범위는 요구하지 않는다(서버 claim_ledger._require 와 같다).
 */
export function registerBlockers({ draft, rows, reason, preview }) {
  const out = [];
  const exempt = draft.fault === 'shipper';
  if (!draft.reason_id && !exempt) out.push('사유');
  if (!draft.fault) out.push('귀책');
  if (!draft.handling) out.push('처리 방식');
  if (!draft.temp_zone) out.push('품온');
  if (!draft.center_key) out.push('센터');
  const selected = rows.filter((r) => r.selected);
  if (!selected.length || selected.some((r) => (intOrNull(r.qty) ?? 0) < 1)) out.push('클레임 상품(수량 1 이상)');
  if (!exempt && reason && reason.accident_type === 'general' && reason.comp_scope === 'select' && !draft.extent) out.push('범위');
  const calc = preview && preview.calc;
  if (calc && calc.blocked) out.push('처리 불가 조합');
  if (calc && calc.manual_required && calc.manual_required.length) out.push('직접 입력 필요 금액');
  return out;
}

/** 택배사 접수기한 표기 — D-day · 기한 미설정 · 그룹 미확정 · 해당없음. */
export function courierDeadlineText(courier) {
  if (!courier) return '—';
  switch (courier.state) {
    case 'unconfirmed': return '택배사 그룹 미확정';
    case 'unset': return '기한 미설정';
    case 'na': return '해당없음';
    case 'no_ship_date': return '출고일 없음';
    default: {
      const d = courier.d_day;
      const dday = d == null ? '' : d >= 0 ? `D-${d}` : `D+${-d}`;
      const range = courier.start ? `${courier.start} ~ ${courier.end}` : `${courier.end} 까지`;
      return `${dday} ${range}${courier.state === 'overdue' ? ' (경과)' : courier.state === 'not_yet' ? ' (접수 시작 전)' : ''}`.trim();
    }
  }
}

/** 고객사 접수기한 표기. */
export function shipperDeadlineText(shipper) {
  if (!shipper) return '—';
  if (shipper.state === 'unset') return '기한 없음';
  if (shipper.state === 'no_ship_date') return '출고일 없음';
  return `${shipper.date} 까지${shipper.overdue ? ' — 접수기한 경과' : ''}`;
}

/** 제품가 근거 표기. */
export function productBasisText(calc) {
  if (!calc) return '';
  if (calc.product_comp === 'X') return '제품가 미변상';
  switch (calc.product_basis) {
    case 'sale_net': return `결제금액 ÷ ${(1 + (Number(calc.vat_pct) || 0) / 100).toFixed(1)}(부가세 공제)`;
    case 'sale_ratio': return `판매가 × ${calc.sale_ratio_pct ?? '—'}%`;
    case 'purchase_cost': return '매입가 기준';
    case 'manual': return '직접 입력';
    default: return '';
  }
}
